import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { MessageSquare, X } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useApp } from '../state/AppContext';
import type { ChatToastTarget } from '../state/toasts';
import { useUnreadMessages } from '../state/unreadMessages';
import { Skeleton, SkeletonBlock } from './Skeleton';
import './chatFab.css';

const MessagesPanel = lazy(() =>
  import('../community/CommunityPages').then((module) => ({ default: module.MessagesPage })),
);

/** A single chat entry point shared by public, loading and member pages. */
export function ChatFab({ request }: { request?: ChatToastTarget }) {
  const { mode, session, state, accountReady } = useApp();
  const location = useLocation();
  const signedIn = mode === 'demo' || Boolean(session);
  const onMessagesPage =
    location.pathname === '/messages' || location.pathname.startsWith('/messages/');
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState<ChatToastTarget>();
  const popupRef = useRef<HTMLDivElement>(null);
  const handledRequest = useRef<ChatToastTarget | undefined>(undefined);
  const viewerId = session?.user.id;
  const unreadIds = state.conversations
    .filter((item) => item.unread_count > 0)
    .map((item) => item.id)
    .join(',');
  const hasLiveUnread = useUnreadMessages(accountReady ? viewerId : undefined, unreadIds);
  const hasUnread = mode === 'demo' ? Boolean(unreadIds) : hasLiveUnread;
  const next = encodeURIComponent(location.pathname + location.search || '/messages');
  const closeChat = useCallback(() => {
    setTarget(undefined);
    setOpen(false);
  }, []);
  useEffect(closeChat, [viewerId, closeChat]);
  useEffect(() => {
    if (onMessagesPage) closeChat();
  }, [onMessagesPage, closeChat]);
  useEffect(() => {
    if (!request || handledRequest.current === request) return;
    handledRequest.current = request;
    if (request.viewerId !== viewerId) return;
    setTarget(request);
    setOpen(true);
  }, [request, viewerId]);
  useEffect(() => {
    if (!open) return;
    popupRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !document.querySelector('dialog[open]')) closeChat();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, target, closeChat]);
  if (onMessagesPage && signedIn && !open) return null;
  const label = open ? 'Close chat' : hasUnread ? 'Open chat, unread messages' : 'Open chat';
  return (
    <>
      {open && (
        <div
          id="chat-popup"
          ref={popupRef}
          className="chat-popup"
          role="dialog"
          aria-label="Messages"
          tabIndex={-1}
        >
          <div className="chat-popup-head">
            <strong>Messages</strong>
            <button
              type="button"
              className="chat-popup-close"
              aria-label="Close chat"
              onClick={closeChat}
            >
              <X size={18} aria-hidden="true" />
            </button>
          </div>
          <div className="chat-popup-body">
            {signedIn ? (
              <Suspense
                fallback={
                  <div className="chat-embed">
                    <div className="chat-embed-bar" aria-hidden="true">
                      <SkeletonBlock style={{ width: 88, height: 12 }} />
                    </div>
                    <Skeleton variant="messages" count={4} label="Loading messages…" />
                  </div>
                }
              >
                <MessagesPanel
                  key={viewerId}
                  embedded
                  requestedConversation={target?.viewerId === viewerId ? target : undefined}
                />
              </Suspense>
            ) : (
              <div className="chat-guest">
                <p>Chat is not available when being logged out. Please Sign Up or Login.</p>
                <div className="chat-guest-actions">
                  <Link className="chat-guest-login" to={`/auth/signin?next=${next}`}>
                    Login
                  </Link>
                  <Link className="chat-guest-signup" to="/auth/register">
                    Sign Up
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      <button
        type="button"
        className={`chat-fab${open ? ' is-open' : ''}`}
        aria-expanded={open}
        aria-controls="chat-popup"
        aria-label={label}
        title={label}
        onClick={() => (open ? closeChat() : setOpen(true))}
      >
        {open ? <X size={24} aria-hidden="true" /> : <MessageSquare size={24} aria-hidden="true" />}
        {!open && hasUnread && <span className="chat-fab-unread" aria-hidden="true" />}
      </button>
    </>
  );
}
